import styled from '@emotion/styled';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import GenericButton from 'src/components/GenericButton';
import ModalLayout, { MobileModalType, ModalFooter, ModalHeader, ModalHeaderTitle } from 'src/components/ModalLayout';
import ProfileReportModal, { Props as ProfileReportModalProps } from 'src/components/Report/ProfileReportModal';
import { DM_QUERY_KEYS } from 'src/hooks/useDirectMessageQuery';
import { FOLLOW_QUERY_KEYS } from 'src/hooks/useFollowQuery';
import { closeModalAtom, showModalAtom } from 'src/stores/modal/atoms';
import { ReportProfileCardAPI } from 'src/stores/report/apis';
import { showToastAtom } from 'src/stores/toast/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { ModalType } from 'src/types/Modal';
import { REPORT_EXCEPT_DSA, ReportType } from 'src/types/report';
import { ToastType } from 'src/types/Toast';
import { ControlledPromise } from 'src/utils/controlled-promise';
import { pushEvent } from 'src/utils/event-gateway';
const StyledModalFooter = styled(ModalFooter)`
  flex-direction: row;
  gap: 16px;
`;
const Button = styled(GenericButton)`
  flex: 1;
`;
type Props = Omit<ProfileReportModalProps, 'reportTypes'> & {
  onSuccess?: () => void;
};
export default function ReportAndBlockPopup({
  cardId,
  cardType,
  eventParams,
  onSuccess,
  ...props
}: Props) {
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const showModal = useSetAtom(showModalAtom);
  const showToast = useSetAtom(showToastAtom);
  const closeModal = useSetAtom(closeModalAtom);
  const {
    mutate,
    isPending
  } = useMutation({
    async mutationFn() {
      const controlledPromise = new ControlledPromise<ReportType>();
      showModal({
        key: ModalType.REPORT,
        component: () => <ProfileReportModal cardId={cardId} cardType={cardType} eventParams={eventParams} onReport={controlledPromise.resolve.bind(controlledPromise)} reportTypes={REPORT_EXCEPT_DSA} {...props} onClose={controlledPromise.reject.bind(controlledPromise)} />
      });
      let reportType: ReportType;

      /** Dismiss 할 경우 early return */
      try {
        reportType = await controlledPromise.promise;
      } catch {
        return;
      }
      try {
        await ReportProfileCardAPI({
          reportType,
          cardId,
          cardType
        });
        showToast({
          message: 'REPORT_COMPLETED',
          type: ToastType.SUCCESS
        });
        queryClient.invalidateQueries({
          queryKey: DM_QUERY_KEYS.dmRoomList(),
          refetchType: 'all'
        });
        queryClient.invalidateQueries({
          queryKey: FOLLOW_QUERY_KEYS.follow(),
          refetchType: 'all'
        });
        closeModal(ModalType.REPORT_AND_BLOCK_POPUP);
        onSuccess?.();
        return {
          reportType
        };
      } catch {
        showToast({
          message: 'REPORT_FAILED',
          type: ToastType.ERROR
        });
      } finally {
        closeModal(ModalType.REPORT);
      }
    }
  });
  const handleReport = () => {
    pushEvent({
      eventType: EVENT_TYPE.REPORT,
      eventName: EVENT_NAME.REPORT_BLOCK_SELECT_POPUP_SELECT,
      eventParams: {
        ...eventParams,
        action_category: 'click',
        target: 'report'
      }
    });
    mutate(undefined, {
      onSuccess: result => {
        if (result) {
          pushEvent({
            eventType: EVENT_TYPE.REPORT,
            eventName: EVENT_NAME.REPORT_USER,
            eventParams: {
              ...eventParams,
              action_category: 'action',
              target: `report.${result.reportType}`
            }
          });
        }
      }
    });
  };
  const handleBlock = () => {
    pushEvent({
      eventType: EVENT_TYPE.REPORT,
      eventName: EVENT_NAME.REPORT_BLOCK_SELECT_POPUP_SELECT,
      eventParams: {
        ...eventParams,
        action_category: 'click',
        target: 'block'
      }
    });
    mutate(undefined, {
      onSuccess: result => {
        if (result) {
          pushEvent({
            eventType: EVENT_TYPE.REPORT,
            eventName: EVENT_NAME.BLOCK_USER,
            eventParams: {
              ...eventParams,
              action_category: 'action',
              target: `block.${result.reportType}`
            }
          });
        }
      }
    });
  };
  return <ModalLayout isDim mobileType={MobileModalType.BOTTOM_SHEET} data-sentry-element="ModalLayout" data-sentry-component="ReportAndBlockPopup" data-sentry-source-file="index.tsx">
      <ModalHeader data-sentry-element="ModalHeader" data-sentry-source-file="index.tsx">
        <ModalHeaderTitle mobileType={MobileModalType.BOTTOM_SHEET} data-sentry-element="ModalHeaderTitle" data-sentry-source-file="index.tsx">
          {t('SHEET_REPORTANDBLOCK')}
        </ModalHeaderTitle>
      </ModalHeader>
      <StyledModalFooter data-sentry-element="StyledModalFooter" data-sentry-source-file="index.tsx">
        <Button size='large' variant='tertiary' onClick={handleReport} disabled={isPending} data-sentry-element="Button" data-sentry-source-file="index.tsx">
          {t('BUTTON__REPORT')}
        </Button>
        <Button size='large' variant='accent' onClick={handleBlock} disabled={isPending} data-sentry-element="Button" data-sentry-source-file="index.tsx">
          {t('BUTTON__BLOCK')}
        </Button>
      </StyledModalFooter>
    </ModalLayout>;
}